import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { AdAudienceResult } from '@/services/auth/types';
import { SHARED_QUERY_OPTIONS } from './constants';
import { filterAudienceCredentials, mapAudienceCredentialToSearchResult } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseAdAudiencesSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type AdAudiencesSource = SearchSource<AdAudienceResult>;

export function useAdAudiencesSource({ workspaceId, enabled, query }: UseAdAudiencesSourceOptions): AdAudiencesSource {
  const adAudiencesQuery = useQuery(
    getServicesQueryOptions(
      'getAudienceCredentials',
      { params: { environment_id: workspaceId } },
      {
        enabled,
        ...SHARED_QUERY_OPTIONS,
        select: (data) => data.audience_credentials,
      },
    ),
  );

  const results = useMemo(
    () =>
      filterAudienceCredentials(adAudiencesQuery.data ?? [], query).map((credential) =>
        mapAudienceCredentialToSearchResult(credential, workspaceId),
      ),
    [adAudiencesQuery.data, query, workspaceId],
  );

  return { ...adAudiencesQuery, results };
}
